// ── Boosted Posts Configuration ───────────────────────────────────
//
// BID SYSTEM: Users name their own price. Higher bids get better
// feed placement. All boosts last 6 hours. The market sets the price.
//
import { PLATFORM_FEE_ADDRESS } from './platform';
import { auditClaim } from './paymentAudit';

// Boost payment goes to the platform wallet
export const BOOST_WALLET = PLATFORM_FEE_ADDRESS;

// Nostr event tracking
export const BOOST_KIND = 30078;
export const BOOST_D_TAG_PREFIX = 'bchnostr/boost/'; // + event id
export const BOOST_TAG = 'bch-boost';

// ── Bid Configuration ────────────────────────────────────────────
// All boosts last 6 hours. Shorter duration = more turnover, fresher feed, more revenue.
export const BOOST_DURATION = 6 * 3600; // 6 hours in seconds

// Maximum allowed duration for any boost (sanity cap: 7 days).
// Any expiresAt more than this far in the future is treated as invalid/expired.
const MAX_BOOST_DURATION = 7 * 86400;

// Minimum bid in USD — low enough to be accessible
export const BOOST_MIN_BID_USD = 0.25;

// Suggested bid presets (USD) — quick-tap buttons
export const BOOST_BID_PRESETS = [
  { usd: 0.25, label: '$0.25',  emoji: '\u26A1' },
  { usd: 1,    label: '$1',     emoji: '\u{1F525}' },
  { usd: 3,    label: '$3',     emoji: '\u{1F680}' },
  { usd: 5,    label: '$5',     emoji: '\u{1F4A5}' },
  { usd: 10,   label: '$10',    emoji: '\u{1F451}' },
];

// Feed placement slots — boosted posts are inserted at these positions.
// Higher bids get lower slot numbers (closer to top of feed).
// Max visible boosted posts at a time.
export const BOOST_MAX_VISIBLE = 5;
export const BOOST_SLOT_POSITIONS = [2, 5, 9, 14, 20]; // feed positions

/** Convert a USD price to sats given the current BCH price. Returns null if price unavailable. */
export function usdToSats(usd: number, bchPriceUsd: number | null): number | null {
  if (!bchPriceUsd || bchPriceUsd <= 0) return null;
  return Math.ceil((usd / bchPriceUsd) * 1e8);
}

/** Convert sats to USD string */
export function satsToUsdStr(sats: number, bchPriceUsd: number | null): string | null {
  if (!bchPriceUsd || bchPriceUsd <= 0) return null;
  const usd = (sats / 1e8) * bchPriceUsd;
  if (usd < 0.01) return '<$0.01';
  return '$' + usd.toFixed(2);
}

// ── Legacy tier support (keep for old boost events still in the wild) ──
export const BOOST_TIERS = [
  { id: '1h',  label: '1 Hour',   duration: 3600,       priceUsd: 2, emoji: '\u26A1' },
  { id: '6h',  label: '6 Hours',  duration: 6 * 3600,   priceUsd: 4, emoji: '\u{1F525}' },
  { id: '24h', label: '24 Hours', duration: 24 * 3600,  priceUsd: 8, emoji: '\u{1F680}' },
];

// Poll boost tiers
export const POLL_BOOST_TAG = 'bch-poll-boost';
export const POLL_BOOST_TIERS = [
  { id: '1h',  label: '1 Hour',   duration: 3600,       priceUsd: 2, emoji: '\u26A1' },
  { id: '6h',  label: '6 Hours',  duration: 6 * 3600,   priceUsd: 4, emoji: '\u{1F525}' },
  { id: '24h', label: '24 Hours', duration: 24 * 3600,  priceUsd: 8, emoji: '\u{1F680}' },
];

// Cash Clips boost config
export const CLIP_BOOST_TAG = 'bch-clip-boost';
export const CLIP_BOOST_DURATIONS = [
  { id: '6h',  label: '6 Hours',  duration: 6 * 3600,  emoji: '\u26A1' },
  { id: '24h', label: '24 Hours', duration: 24 * 3600, emoji: '\u{1F680}' },
];

// ── Creator Goal boost ───────────────────────────────────────────
// Members can pay BCH to draw attention to a creator goal. Because a
// creator has ONE active goal at a time (the goal event is addressable and
// gets replaced on every edit), a goal boost is keyed by the *creator's
// pubkey* rather than a specific event id — so an edit to the goal doesn't
// silently invalidate an active boost. Boosted goals float to the top of
// the goals list and get a "Boosted" badge for the duration.
export const GOAL_BOOST_TAG = 'bch-goal-boost';
export const GOAL_BOOST_D_TAG_PREFIX = 'bchnostr/goal-boost/'; // + creator pubkey
export const GOAL_BOOST_DURATION = 6 * 3600; // 6h
export const GOAL_BOOST_MIN_BID_USD = 0.5;
export const GOAL_BOOST_BID_PRESETS = [
  { usd: 0.5, label: '$0.50', emoji: '\u26A1' },
  { usd: 1,   label: '$1',    emoji: '\u{1F525}' },
  { usd: 3,   label: '$3',    emoji: '\u{1F680}' },
  { usd: 5,   label: '$5',    emoji: '\u{1F451}' },
];

/**
 * Data payload for a goal-boost event (kind 30078, t=bch-goal-boost).
 * `creatorPubkey` is the goal owner being promoted; `boostedBy` is whoever
 * paid (which may be a supporter, not the creator).
 */
export interface GoalBoostData {
  creatorPubkey: string;
  priceSats: number;
  expiresAt: number;
  boostedBy: string;
  bidUsd?: number;
  /** Payment txid, read from the event's `txid` tag when present. */
  txid?: string;
}

/** Parse + sanitize a goal-boost event. Returns null if malformed. */
export function parseGoalBoostData(ev: any): GoalBoostData | null {
  try {
    const data: GoalBoostData = JSON.parse(ev.content);
    if (!data || typeof data.creatorPubkey !== 'string') return null;
    data.expiresAt = sanitizeExpiresAt(data.expiresAt, ev.created_at);
    // The paying re-publish records the txid in a tag; surface it so the
    // boost can be verified against the chain.
    if (!data.txid && Array.isArray(ev.tags)) {
      const t = ev.tags.find((x: string[]) => x[0] === 'txid');
      if (t && typeof t[1] === 'string') data.txid = t[1];
    }
    return data;
  } catch {
    return null;
  }
}

// ── On-chain verification ─────────────────────────────────────────
// A boost EVENT is self-signed and proves nothing about payment: anyone can
// publish one without sending BCH, and even the honest path publishes the
// event BEFORE the payment (so a failed/abandoned payment still leaves an
// event). To trust a boost we must confirm its txid actually paid the boost
// wallet. This checks the Bitcoin Cash chain (reusing the NIP-05 audit).
export type BoostVerifyResult =
  /** Confirmed (or 0-conf) payment of a sufficient amount to the boost wallet. */
  | 'paid'
  /** No txid, wrong recipient, or underpaid — definitively not a valid boost. */
  | 'unpaid'
  /** Tx not on-chain yet: either a fresh mempool tx or a fabricated txid. */
  | 'not-found'
  /** Explorers unreachable — transient. */
  | 'unavailable';

/**
 * Verify that `txid` paid at least `expectedSats` to the boost wallet.
 * The caller decides how to treat 'not-found' / 'unavailable' (see
 * useVerifiedGoalBoosts, which gives fresh boosts a short grace window).
 */
export async function verifyBoostPayment(
  txid: string | undefined,
  expectedSats: number,
): Promise<BoostVerifyResult> {
  if (!txid || !/^[0-9a-f]{64}$/i.test(txid.trim())) return 'unpaid';
  try {
    const res = await auditClaim(
      { name: txid, pubkey: '', claimedAt: 0, txid: txid.trim().toLowerCase(), amountSats: expectedSats },
      BOOST_WALLET,
      expectedSats,
    );
    switch (res.verdict) {
      case 'paid': return 'paid';
      case 'not-found': return 'not-found';
      case 'unavailable': return 'unavailable';
      // underpaid / wrong-recipient / duplicate / no-record → not a valid boost
      default: return 'unpaid';
    }
  } catch {
    return 'unavailable';
  }
}

/** Is a goal-boost event still active? */
export function isGoalBoostActive(ev: any): boolean {
  const data = parseGoalBoostData(ev);
  if (!data) return false;
  return data.expiresAt > Math.floor(Date.now() / 1000);
}

/**
 * Given a list of goal-boost events, return creatorPubkey → the active boost
 * with the highest bid (used to rank boosted goals and show the badge).
 */
export function getActiveGoalBoosts(events: any[]): Map<string, GoalBoostData> {
  const map = new Map<string, GoalBoostData>();
  for (const ev of events) {
    const data = parseGoalBoostData(ev);
    if (!data) continue;
    if (data.expiresAt <= Math.floor(Date.now() / 1000)) continue;
    const existing = map.get(data.creatorPubkey);
    // Highest bid wins the badge; ties broken by latest expiry.
    if (!existing || data.priceSats > existing.priceSats ||
        (data.priceSats === existing.priceSats && data.expiresAt > existing.expiresAt)) {
      map.set(data.creatorPubkey, data);
    }
  }
  return map;
}

// Marketplace featured listing tiers
export const FEATURED_LISTING_TAG = 'bch-marketplace-featured';
export const FEATURED_LISTING_TIERS = [
  { id: '3d',  label: '3 Days',  duration: 3 * 86400,  priceSats: 100000,  emoji: '⭐' },
  { id: '7d',  label: '7 Days',  duration: 7 * 86400,  priceSats: 300000,  emoji: '\u{1F525}' },
  { id: '30d', label: '30 Days', duration: 30 * 86400, priceSats: 1000000, emoji: '\u{1F680}' },
];

export interface BoostData {
  eventId: string;
  tier: string;       // 'bid' for new system, '1h'/'6h'/'24h' for legacy
  priceSats: number;  // the bid amount in sats
  expiresAt: number;  // unix timestamp (seconds)
  boostedBy: string;  // pubkey
  bidUsd?: number;    // USD value at time of bid (for display)
}

/**
 * Sanitize expiresAt — fix common bugs:
 * - Milliseconds instead of seconds (divide by 1000)
 * - Absurdly far in the future (cap to MAX_BOOST_DURATION from now)
 * - In the past (return as-is, isBoostActive will reject it)
 * - Not a number (return 0)
 */
function sanitizeExpiresAt(expiresAt: any, createdAt?: number): number {
  if (typeof expiresAt !== 'number' || !isFinite(expiresAt) || expiresAt <= 0) return 0;

  const now = Math.floor(Date.now() / 1000);

  // Detect milliseconds: if expiresAt is > 10 billion, it's likely in ms
  // (Unix seconds won't reach 10B until year 2286)
  if (expiresAt > 10_000_000_000) {
    expiresAt = Math.floor(expiresAt / 1000);
  }

  // Cap to max duration from event creation time (or now, whichever is later)
  const reference = createdAt && createdAt > 0 ? createdAt : now;
  const maxExpiry = reference + MAX_BOOST_DURATION;
  if (expiresAt > maxExpiry) {
    expiresAt = maxExpiry;
  }

  return expiresAt;
}

/** Check if a boost event is still active */
export function isBoostActive(boostEvent: any): boolean {
  try {
    const data: BoostData = JSON.parse(boostEvent.content);
    const expiresAt = sanitizeExpiresAt(data.expiresAt, boostEvent.created_at);
    return expiresAt > Math.floor(Date.now() / 1000);
  } catch {
    return false;
  }
}

/** Parse boost data from a boost event, with sanitized expiresAt */
export function parseBoostData(boostEvent: any): BoostData | null {
  try {
    const data: BoostData = JSON.parse(boostEvent.content);
    // Sanitize expiresAt to fix ms/seconds confusion and absurd values
    data.expiresAt = sanitizeExpiresAt(data.expiresAt, boostEvent.created_at);
    return data;
  } catch {
    return null;
  }
}

/**
 * Given a list of raw boost events, return the pubkey → BoostData map
 * for pubkeys that currently have an active boost.
 *
 * NOTE: This maps pubkey → their boost with the latest expiry.
 * A user can have multiple active boosts for different posts.
 * The "one active boost" rule is enforced at the UI level per-post.
 */
export function getActivePubkeyBoosts(boostEvents: any[]): Map<string, BoostData> {
  const map = new Map<string, BoostData>();
  boostEvents.forEach(ev => {
    if (!isBoostActive(ev)) return;
    const data = parseBoostData(ev);
    if (!data) return;
    // Keep whichever boost expires latest for this pubkey
    const existing = map.get(ev.pubkey);
    if (!existing || data.expiresAt > existing.expiresAt) {
      map.set(ev.pubkey, data);
    }
  });
  return map;
}

/**
 * Get ALL active boosts for a specific pubkey (not just the latest).
 * Returns a map of eventId → BoostData.
 */
export function getAllActiveBoostsForPubkey(boostEvents: any[], pubkey: string): Map<string, BoostData> {
  const map = new Map<string, BoostData>();
  boostEvents.forEach(ev => {
    if (ev.pubkey !== pubkey) return;
    if (!isBoostActive(ev)) return;
    const data = parseBoostData(ev);
    if (!data) return;
    // For the same eventId (same d-tag), keep only the newest
    const existing = map.get(data.eventId);
    if (!existing || data.expiresAt > existing.expiresAt) {
      map.set(data.eventId, data);
    }
  });
  return map;
}

/**
 * Sort active boosts by bid amount (highest first).
 * This determines feed placement order — highest bidder gets best slot.
 */
export function sortBoostsByBid(boosts: BoostData[]): BoostData[] {
  return [...boosts].sort((a, b) => b.priceSats - a.priceSats);
}

/**
 * Get the current "leaderboard" of active boosts for display in the bid UI.
 * Returns sorted by bid amount (highest first), capped at maxVisible.
 */
export function getActiveBoostLeaderboard(
  boostEvents: any[],
  bchPrice: number | null,
  maxVisible = BOOST_MAX_VISIBLE,
): { eventId: string; bidSats: number; bidUsd: string | null; rank: number; expiresAt: number }[] {
  const active: BoostData[] = [];
  const seenEvents = new Set<string>();

  // Collect all active boosts, one per event ID (latest version wins)
  for (const ev of boostEvents) {
    if (!isBoostActive(ev)) continue;
    const data = parseBoostData(ev);
    if (!data || seenEvents.has(data.eventId)) continue;
    seenEvents.add(data.eventId);
    active.push(data);
  }

  return sortBoostsByBid(active)
    .slice(0, maxVisible)
    .map((d, i) => ({
      eventId: d.eventId,
      bidSats: d.priceSats,
      bidUsd: satsToUsdStr(d.priceSats, bchPrice),
      rank: i + 1,
      expiresAt: d.expiresAt,
    }));
}

